import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

// How long the insight usually takes to write, from the run being saved.
// Measured on UAT (200 runs, Oct 2026): median 20s, 90% within 22s, max 25s.
export const INSIGHT_EXPECTED_SECONDS = 25;
// The bar never reaches 100% on time alone — the insight arriving does that.
const MAX_ESTIMATED_PCT = 95;

// What the insight covers, revealed in order as time passes. Purely a guide
// to the wait: there is no live progress signal from the workflow.
export const INSIGHT_STEPS = [
  { at: 0, label: "Reading the forecast" },
  { at: 25, label: "Checking stock and reorder point" },
  { at: 50, label: "Comparing warehouses" },
  { at: 75, label: "Writing the summary" },
];
// The product artifact (whole-product analysis) takes much longer.
export const ARTIFACT_EXPECTED_SECONDS = 120;
export const ARTIFACT_STEPS = [
  { at: 0, label: "Collecting every SKU's forecast" },
  { at: 25, label: "Checking stock cover per SKU" },
  { at: 50, label: "Finding issues and risks" },
  { at: 75, label: "Writing recommendations" },
];
// The explanation is written in the same workflow run as the insight.
export const EXPLANATION_STEPS = [
  { at: 0, label: "Reading the sales history" },
  { at: 25, label: "Checking the method chosen" },
  { at: 50, label: "Working through the numbers" },
  { at: 75, label: "Writing the explanation" },
];

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.75; }
`;
const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
`;
const noMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

/**
 * Loader for an insight that's still being written, timed against the usual
 * duration: the bar fills towards 95% over INSIGHT_EXPECTED_SECONDS from when
 * the run was saved, the steps tick off along the way, and past that it says
 * it's taking longer. The real insight replaces it the moment it arrives.
 */
export default function InsightProgress({
  startedAt,
  ui,
  title = "Writing your DropSkip Insight",
  steps = INSIGHT_STEPS,
  label = "Insight progress",
  expectedSeconds = INSIGHT_EXPECTED_SECONDS,
  waitText = "Usually ready in about 20 seconds. You can keep working; it fills in on its own.",
}) {
  const { colors } = ui;
  const start = startedAt ? new Date(startedAt).getTime() : NaN;
  const [now, setNow] = useState(() => Date.now());
  const [mountedAt] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  // A run saved long before the page opened (or a clock in the future)
  // counts from now instead, so the bar never starts full or negative.
  const from = Number.isFinite(start) && start <= now ? start : mountedAt;
  const elapsed = Math.max(0, (now - from) / 1000);
  const pct = Math.min(MAX_ESTIMATED_PCT, (elapsed / expectedSeconds) * 100);
  const left = Math.ceil(expectedSeconds - elapsed);
  const overdue = left <= 0;
  const activeStep = steps.reduce((acc, s, i) => (pct >= s.at ? i : acc), 0);

  return (
    <Box
      aria-busy="true"
      aria-live="polite"
      sx={{
        ...ui.card,
        p: { xs: "14px 16px", sm: "16px 20px" },
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        background: `linear-gradient(135deg, ${ui.accentSoft} 0%, ${colors.bgPaper} 55%)`,
      }}
    >
      {/* Header: icon, title, time left */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: ui.accent,
            color: "#fff",
            boxShadow: `0 0 0 4px ${ui.accentSoft}`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 19, animation: `${pulse} 1.8s ease-in-out infinite`, ...noMotion }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 650, color: colors.textPrimary, lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
            {overdue
              ? "Taking a little longer than usual — it will appear here as soon as it's ready."
              : waitText}
          </Typography>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            px: 1.25,
            py: 0.4,
            borderRadius: 999,
            bgcolor: overdue ? ui.warningBg : ui.surfaceAlt,
            color: overdue ? ui.warning : colors.textSecondary,
            fontSize: 11.5,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {overdue
            ? "Almost there"
            : left >= 90
              ? `~${Math.ceil(left / 60)} min left`
              : `~${left}s left`}
        </Box>
      </Box>

      {/* Progress bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          sx={{ position: "relative", flex: 1, height: 8, borderRadius: 999, bgcolor: ui.surfaceAlt, overflow: "hidden" }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${pct}%`,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${ui.accent} 0%, ${ui.darkMode ? "#8FB3FF" : "#6E8FF2"} 100%)`,
              transition: "width 0.5s linear",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                width: "40%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
                animation: `${shimmer} 1.6s ease-in-out infinite`,
                ...noMotion,
              },
            }}
          />
        </Box>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: colors.textSecondary, minWidth: 34, textAlign: "right" }}
        >
          {Math.round(pct)}%
        </Typography>
      </Box>

      {/* Steps */}
      <Box
        component="ol"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: { xs: 1, md: 1.5 },
        }}
      >
        {steps.map((step, i) => {
          const done = i < activeStep;
          const active = i === activeStep;
          return (
            <Box
              component="li"
              key={step.label}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
            >
              {done ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 17, color: ui.positive, flexShrink: 0 }} />
              ) : active ? (
                <CircularProgress aria-hidden="true" size={14} thickness={6} sx={{ color: ui.accent, flexShrink: 0, mx: "1.5px" }} />
              ) : (
                <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 17, color: ui.borderStrong, flexShrink: 0 }} />
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.3,
                  fontWeight: active ? 600 : 400,
                  color: done || active ? colors.textPrimary : colors.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={step.label}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Where the insight will appear */}
      <Box sx={{ opacity: 0.55, display: "flex", flexDirection: "column", gap: 0.25 }} aria-hidden="true">
        <Skeleton variant="text" animation="wave" width="94%" />
        <Skeleton variant="text" animation="wave" width="86%" />
        <Skeleton variant="text" animation="wave" width="62%" />
      </Box>
    </Box>
  );
}
